// Router (architect task P0-03, completed in P0-13) — two role-based app
// shells behind one build, lazy-split at the top level (§9.2). The `/app/(auth)`
// group (login/verify/forgot/reset/invite) is public; the shells sit behind the
// role-aware guard below (§9.3 step 4).
import {
  createRouter,
  createWebHistory,
  type NavigationGuardReturn,
  type RouteLocationNormalized,
} from 'vue-router';
import { Role } from '@eskayscience/shared/enums';

import { useAuthStore, homeRouteForRole, type UserRole } from '@/stores/auth';
import { applyPageMeta } from '@/lib/page-meta';

declare module 'vue-router' {
  interface RouteMeta {
    /** Route is reachable without a session (login/invite/reset/verify); the
     * guard also bounces an already-authenticated visitor to their role home. */
    public?: boolean;
    /** Guest-accessible discovery surface (P1-BUG-3): reachable WITHOUT a
     * session so the Landing's guest CTAs work, but — unlike `public` — an
     * authenticated visitor is NOT bounced away, since the student nav links
     * here too. `/library` + `/player` (mock data, no personal state). */
    guest?: boolean;
    /** Roles allowed to view this route; omitted = any authenticated role. */
    roles?: UserRole[];
    /** Reachable by a student whose entitlement has lapsed (the guard normally
     * funnels expired students to `learn-expired`; that page itself opts in). */
    allowExpired?: boolean;
    /** Per-route <title> (PERF-01 item D); falls back to the site default
     * ("Eskay Science") when omitted. Applied via applyPageMeta on every
     * navigation, see router.afterEach below. */
    title?: string;
    /** Per-route <meta name="description"> (PERF-01 item D); falls back to
     * the site-wide default when omitted. */
    description?: string;
  }
}

export const routes = [
  // ---------------------------------------------------------------------------
  // Public marketing screens (P1-17 Landing, P1-18 Plans). Reachable logged-out
  // or in; the guard treats `meta.public` uniformly. The Landing is the site
  // root `/` (marketing origin); `/welcome` stays a permanent alias so existing
  // deep links keep resolving.
  // ---------------------------------------------------------------------------
  {
    path: '/',
    alias: '/welcome',
    name: 'landing',
    component: () => import('@/views/landing/LandingView.vue'),
    meta: {
      public: true,
      title: 'Video Lessons, Simulations & Printables for Every Subject',
      description:
        'Eskay Science is a subscription classroom platform with video lessons, interactive simulations and printable resources for students, teachers and admins.',
    },
  },
  {
    path: '/plans',
    name: 'plans',
    component: () => import('@/views/public/PlansView.vue'),
    meta: {
      // `guest: true` (not `public: true`): unlike the other `public` routes
      // (auth-verify/forgot/reset/invite, Landing), Plans must stay reachable
      // for AUTHENTICATED visitors too — it's a dead link otherwise. `guest`
      // already lets logged-out visitors through (~line 379) while carrying
      // no `roles` restriction, so an authenticated user simply falls through
      // the normal role-check path below instead of being bounced home.
      guest: true,
      // An entitlement-expired student should see pricing (to resubscribe),
      // not get redirected to `learn-expired`.
      allowExpired: true,
      title: 'Plans & Pricing',
      description:
        'Compare Eskay Science subscription plans and choose the one that fits your classroom.',
    },
  },
  {
    // §9.2's "/app/(auth)" group — the parens denote an organizational
    // grouping (shared layout), not a URL segment.
    path: '/app/login',
    name: 'auth-login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: {
      public: true,
      title: 'Sign In',
      description: 'Sign in to your Eskay Science account.',
    },
  },
  {
    path: '/app/register',
    name: 'auth-register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: {
      public: true,
      title: 'Create Account',
      description: 'Create your Eskay Science student account.',
    },
  },
  {
    path: '/app/verify-email',
    name: 'auth-verify',
    component: () => import('@/views/auth/VerifyEmailView.vue'),
    meta: { public: true },
  },
  {
    path: '/app/forgot-password',
    name: 'auth-forgot',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/app/reset-password',
    name: 'auth-reset',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/app/invite/accept',
    name: 'auth-invite',
    component: () => import('@/views/auth/InviteAcceptView.vue'),
    meta: { public: true },
  },
  {
    // ADMIN + TEACHER share the authoring shell (§9.4 / auth-store
    // homeRouteForRole); STUDENT gets the learn shell.
    path: '/app/teacher',
    name: 'teacher-shell',
    component: () => import('@/views/teacher/TeacherShellView.vue'),
    meta: { roles: [Role.ADMIN, Role.TEACHER] },
  },
  // ---------------------------------------------------------------------------
  // Authoring-UI screens (TeacherShell). ADMIN + TEACHER share the authoring
  // shell (§9.4); the guard bounces STUDENT/guest. These are pre-registered
  // fill-targets so their owning tasks land known paths without touching the
  // frozen router:
  //  - `board-level-manager` (P1-10b) — the board/level manager strip (§9.4).
  //    [path inferred from §9.4 — provisional]
  //  - `subject-builder` (P1-11) — the two-pane section-tree + tab editor,
  //    keyed by the subject `:id`. [explicit per the P1-11 ticket]
  // ---------------------------------------------------------------------------
  {
    path: '/app/teacher/boards',
    name: 'board-level-manager',
    component: () => import('@/views/teacher/BoardLevelManagerView.vue'),
    meta: { roles: [Role.ADMIN, Role.TEACHER] },
  },
  {
    path: '/app/teacher/subjects/:id/builder',
    name: 'subject-builder',
    component: () => import('@/views/teacher/SubjectBuilderView.vue'),
    meta: { roles: [Role.ADMIN, Role.TEACHER] },
  },
  // P3-10 — the roster/enrollment UI (§9.2): student list + invite/enroll/
  // extend/revoke + CSV import, and a per-student access-grants/activity/
  // access-log detail screen. `/v1/students` + `/v1/enrollments` were widened
  // from ADMIN-only to TEACHER_OR_ADMIN server-side (a2ed660) — a TEACHER can
  // now list/invite/enroll/extend/revoke here for real. `/v1/access-grants`
  // stays ADMIN-only (ADR-001); the detail screen gates that tab to ADMIN in
  // the UI (`TeacherStudentDetailView.vue`'s `isAdmin`) since it's guaranteed
  // to 403 for a TEACHER. The UI never relies on hiding a control alone for
  // security — mutations still get a real RFC 7807 403 if the server disagrees.
  {
    path: '/app/teacher/students',
    name: 'teacher-students',
    component: () => import('@/views/teacher/TeacherStudentsView.vue'),
    meta: { roles: [Role.ADMIN, Role.TEACHER] },
  },
  {
    path: '/app/teacher/students/:id',
    name: 'teacher-student-detail',
    component: () => import('@/views/teacher/TeacherStudentDetailView.vue'),
    meta: { roles: [Role.ADMIN, Role.TEACHER] },
  },
  // Package authoring (ADR-packages-entitlements.md §6.1/§9 Slice C) —
  // `/v1/packages` CRUD is TEACHER_OR_ADMIN server-side; same UI-mirrors-
  // server-gate pattern as boards/students above.
  {
    path: '/app/teacher/packages',
    name: 'teacher-packages',
    component: () => import('@/views/teacher/PackagesView.vue'),
    meta: { roles: [Role.ADMIN, Role.TEACHER] },
  },
  // P2-20/P2-08 video slice — TEACHER-preview real HLS playback for a single
  // `MediaAsset`, ahead of the full P2-08 resource-detail 4-tab shell (which
  // will supersede/absorb this route once it lands). Gated the same as the
  // rest of the authoring shell since `POST /v1/media-assets/:id/manifest-url`
  // is TEACHER_OR_ADMIN-only.
  {
    path: '/app/teacher/video/:mediaAssetId',
    name: 'teacher-video-preview',
    component: () => import('@/views/player/VideoResourceView.vue'),
    meta: { roles: [Role.ADMIN, Role.TEACHER] },
  },
  // ---------------------------------------------------------------------------
  // Admin screens (TeacherShell / admin layout). ADMIN-only; TEACHER/STUDENT
  // are bounced to their role home by the guard.
  // ---------------------------------------------------------------------------
  {
    path: '/admin',
    name: 'admin-overview',
    component: () => import('@/views/admin/AdminOverviewView.vue'),
    meta: { roles: [Role.ADMIN] },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/UsersView.vue'),
    meta: { roles: [Role.ADMIN] },
  },
  // Admin · User detail (admin-user-detail-page task) — per-account view
  // (info, "Set password", Lock/Unlock) reached by clicking a row on
  // `/admin/users`. ADMIN-only, same guard as the list above.
  {
    path: '/admin/users/:id',
    name: 'admin-user-detail',
    component: () => import('@/views/admin/AdminUserDetailView.vue'),
    meta: { roles: [Role.ADMIN] },
  },
  {
    path: '/admin/revenue',
    name: 'admin-revenue',
    component: () => import('@/views/admin/RevenueView.vue'),
    meta: { roles: [Role.ADMIN] },
  },
  {
    path: '/admin/roster',
    name: 'admin-roster',
    component: () => import('@/views/admin/RosterView.vue'),
    meta: { roles: [Role.ADMIN] },
  },
  {
    path: '/admin/settings',
    name: 'admin-settings',
    component: () => import('@/views/admin/SettingsView.vue'),
    meta: { roles: [Role.ADMIN] },
  },
  {
    path: '/app/learn',
    name: 'learn-shell',
    component: () => import('@/views/learn/LearnShell.vue'),
    meta: { roles: [Role.STUDENT] },
  },
  {
    path: '/app/learn/expired',
    name: 'learn-expired',
    component: () => import('@/views/learn/ExpiredView.vue'),
    meta: { roles: [Role.STUDENT], allowExpired: true },
  },
  // ---------------------------------------------------------------------------
  // Student learn surface screens (LearnShell). `/home` + `/my` are personal
  // student surfaces (STUDENT-only; an expired student is funnelled to
  // `learn-expired` by the guard unless the route opts in). `/library` +
  // `/player` are PUBLIC discovery surfaces (P1-BUG-3): the Landing promises
  // guests can browse the catalogue and run sampler sims with no account, so
  // they carry `guest: true` (no required role) — reachable logged-out, and NOT
  // bounced when a student reaches them from the nav. Both use mock data today.
  // ---------------------------------------------------------------------------
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/home/HomeView.vue'),
    meta: { roles: [Role.STUDENT] },
  },
  // Search (P3-09, §9.2 `/app/learn/search`) — FTS5 search across the
  // student's visible sections + resources (P3-07).
  {
    path: '/app/learn/search',
    name: 'learn-search',
    component: () => import('@/views/learn/SearchView.vue'),
    meta: { roles: [Role.STUDENT] },
  },
  // ---------------------------------------------------------------------------
  // Friendly-URL student learn routes (`docs/ADR-friendly-urls.md` §2.2,
  // task T4). `:boardSlug/:levelSlug/:subjectSlug` mirror the exact per-parent
  // unique-index scoping the schema already enforces (ADR §0); `:sectionPath+`
  // is a repeatable segment so a depth-1/2 nested section resolves at
  // `…/:subjectSlug/:chapterSlug/:unitSlug` (`Section_subjectId_parentId_slug_key`,
  // walked one segment per depth by `@/data/learn/slugResolve`'s
  // `resolveSectionIdByPath`).
  //
  // Route-ordering note (ADR §2.2 "reserved board slugs"): vue-router 4 scores
  // static segments above dynamic ones regardless of registration order, so
  // `expired`/`search` (registered above as literal path segments) already
  // win over `:boardSlug` for a 1-2 segment URL and cannot be shadowed by an
  // exam board slugged one of those words. The dynamic routes below need a
  // MINIMUM of 3 segments, so there is no practical collision with those
  // static routes either way. `isReservedBoardSlug()` in the resolver is kept
  // anyway as an explicit defense-in-depth check (treats a reserved
  // `:boardSlug` as unresolvable rather than ever attempting a lookup) — see
  // that module for the full reasoning and the flagged backend
  // authoring-side gap.
  //
  // The legacy cuid-keyed `section-reader` / `subject-contents` route
  // records (and the views' client-side canonicalisation that redirected
  // them here) have been removed now that the site is friendly-URLs-only
  // (removed pre-launch, so no live bookmarks depended on them) — see
  // `docs/ADR-friendly-urls.md` for the amended history.
  {
    path: '/app/learn/:boardSlug/:levelSlug/:subjectSlug',
    name: 'subject-contents-by-slug',
    component: () => import('@/views/learn/ContentsTableView.vue'),
    meta: { roles: [Role.STUDENT] },
  },
  {
    path: '/app/learn/:boardSlug/:levelSlug/:subjectSlug/:sectionPath+',
    name: 'section-reader-by-slug',
    component: () => import('@/views/player/SectionReaderView.vue'),
    meta: { roles: [Role.STUDENT] },
  },
  // Self-service account page (P4-11) — any authenticated role. `allowExpired`
  // so a lapsed student isn't bounced to `learn-expired` before they can
  // manage their own profile/password.
  {
    path: '/app/account',
    name: 'account-profile',
    component: () => import('@/views/account/AccountProfileView.vue'),
    meta: { roles: [Role.STUDENT, Role.TEACHER, Role.ADMIN], allowExpired: true },
  },
  {
    path: '/library',
    name: 'library',
    component: () => import('@/views/library/LibraryView.vue'),
    meta: {
      guest: true,
      title: 'Library',
      description:
        'Browse the Eskay Science catalogue of subjects, videos, simulations and printables.',
    },
  },
  {
    path: '/player/:id?',
    name: 'player',
    component: () => import('@/views/player/PlayerView.vue'),
    meta: { guest: true },
  },
  {
    path: '/my',
    name: 'my-sims',
    component: () => import('@/views/student/MySimsView.vue'),
    meta: { roles: [Role.STUDENT] },
  },
  // Notifications (TOPBAR-T11, PO DECISION #3) — ONE shared page for every
  // role (student/teacher/admin alike): omitting `roles` means "any
  // authenticated role" per the guard above. The view itself picks the right
  // shell chrome (LearnShell/TeacherShell/AdminShell) from the caller's own
  // role. `allowExpired: true` so a student whose entitlement has lapsed can
  // still read/manage their notifications instead of being funnelled to
  // `learn-expired`.
  {
    path: '/app/notifications',
    name: 'notifications',
    component: () => import('@/views/me/NotificationsView.vue'),
    meta: { allowExpired: true },
  },
  // Dev-only FE-00 foundation gallery — registered ONLY in `vite dev`
  // (`import.meta.env.DEV`), so it never ships in a production build and is
  // excluded from every app nav (§ acceptance: "excluded from production nav").
  ...(import.meta.env.DEV
    ? [
        {
          path: '/preview',
          name: 'preview',
          component: () => import('@/views/preview/PreviewView.vue'),
          meta: { public: true },
        },
      ]
    : []),
  { path: '/:pathMatch(.*)*', redirect: '/app/login' },
];

/**
 * Role-aware navigation guard (§9.3 step 4). Pure w.r.t. the auth store so it
 * is unit-testable: given a target route and the current auth snapshot it
 * returns the same value `beforeEach` expects (`true` = allow, a location =
 * redirect).
 *
 * Rules, in order:
 *  - Public route + already authenticated → bounce to the role home (no reason
 *    to sit on the login screen with a live session).
 *  - Public route otherwise → allow.
 *  - Guest route + no session → allow (P1-BUG-3: guests may browse the library /
 *    run sampler sims). An authenticated visitor falls through to the normal
 *    checks below, so the student nav still resolves these routes.
 *  - Protected route + no session → login, preserving `?redirect=`.
 *  - Protected route + expired student → `learn-expired` (unless already there
 *    / on a page that opts into `allowExpired`).
 *  - Protected route + wrong role → the caller's own role home.
 */
export function resolveNavigation(
  to: RouteLocationNormalized,
  auth: {
    isAuthenticated: boolean;
    role: UserRole | null;
    entitlementExpired: boolean;
  },
): NavigationGuardReturn {
  // Dev-only FE-00 gallery: always reachable, regardless of auth state, so a
  // signed-in developer isn't bounced to their role home. Only registered under
  // `import.meta.env.DEV`, so this branch is dead code in production.
  if (to.name === 'preview') return true;

  if (to.meta.public) {
    if (auth.isAuthenticated && auth.role) {
      return { name: homeRouteForRole(auth.role) };
    }
    return true;
  }

  // Guest-accessible discovery surfaces (P1-BUG-3): `/library` + `/player` are
  // reachable WITHOUT a session so the Landing's guest CTAs land instead of
  // bouncing to Sign-in. Unlike `public`, we do NOT bounce an authenticated
  // visitor here — a signed-in student reaches these from the learn nav — so we
  // only short-circuit for guests and otherwise fall through to the role checks.
  if (to.meta.guest && !auth.isAuthenticated) {
    return true;
  }

  if (!auth.isAuthenticated || !auth.role) {
    return { name: 'auth-login', query: { redirect: to.fullPath } };
  }

  const requiredRoles = to.meta.roles;
  const roleAllowed = !requiredRoles || requiredRoles.includes(auth.role);

  if (auth.role === Role.STUDENT && auth.entitlementExpired && !to.meta.allowExpired) {
    return { name: 'learn-expired' };
  }

  if (!roleAllowed) {
    return { name: homeRouteForRole(auth.role) };
  }

  return true;
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const authStore = useAuthStore();
  return resolveNavigation(to, {
    isAuthenticated: authStore.isAuthenticated,
    role: authStore.role,
    entitlementExpired: authStore.entitlementExpired,
  });
});

// Per-route SEO meta (PERF-01 item D) — applied after each successful
// navigation so `document.title` / <meta name="description"> stay in sync
// even on client-side transitions (not just the initial load).
router.afterEach((to) => {
  applyPageMeta({ title: to.meta.title, description: to.meta.description, path: to.path });
});
